import React, { useState, useEffect, useMemo } from "react";
import {
    useTable,
    useSortBy,
    usePagination,
} from "react-table";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faEdit, faEllipsisV, faPlus, faTrash, faThumbsUp, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from 'sweetalert2';

const ItemMasterApproval = () => {
  const navigate = useNavigate();
    const code = localStorage.getItem("code");
    const token = localStorage.getItem("token");
    const name = localStorage.getItem('name');
    const role = localStorage.getItem('role');
    const badgeId = localStorage.getItem('badgeId');
    const email = localStorage.getItem("email");

    // Filters & search persistence using sessionStorage
    const [filters, setFilters] = useState(() => {
        const saved = sessionStorage.getItem("approveInventoryFilters");
        return saved ? JSON.parse(saved) : { group: null, type: null, createdBy: null, status: null };
    });

    const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem("approveInventorySearch") || "");
    const [pageIndex, setPageIndex] = useState(() => {
        const saved = sessionStorage.getItem("approveInventorySearch");
        return saved ? Number(saved) : 0;
    });

    const [items, setItems] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemGroups, setItemGroups] = useState([]);
    const [createdByOptions, setCreatedByOptions] = useState([]);

    // Fetch all items
    const fetchItems = async () => {
        try {
            const response = await apiClient.get(SERVER_BASE + "/get-items-all", {
                params: { centerCode: code },
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data);
            // Extract unique CreatedBy options
            const uniqueCreators = Array.from(
                new Map(
                    response.data
                        .filter((it) => it.createdByFirstName)
                        .map((it) => [
                            it.createdByBadge,
                            {
                                value: it.createdByBadge,
                                label: `${it.createdByFirstName} (${it.createdByBadge})`,
                            },
                        ])
                ).values()
            );
            setCreatedByOptions(uniqueCreators);
        } catch (error) {
            console.error("Error fetching items:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch suggestions
    const fetchSuggestions = async () => {
        try {
            const response = await apiClient.get(SERVER_BASE + "/getInventorySuggestions");
            setSuggestions(response?.data?.suggestions || []);
        } catch (error) {
            console.error("Error fetching suggestion:", error);
        }
    };

    // Fetch item groups
    const fetchItemGroups = async () => {
        try {
            const response = await apiClient.get(SERVER_BASE + "/item-groups", {
                params: { code },
                headers: { Authorization: `Bearer ${token}` },
            });
            setItemGroups(
                response.data.map((group) => ({
                    value: group.groupName,
                    label: `${group.groupCode} - ${group.groupName}`,
                    type: group.type,
                    itemGroupCode: group.groupCode,
                }))
            );
        } catch (error) {
            console.error("Error fetching item groups:", error);
        }
    };

    useEffect(() => {
        fetchItems();
        fetchItemGroups();
        fetchSuggestions();
    }, []);

    // Function to filter items / suggestions
    const applyFilters = (data, filtersObj, search) => {
        // If status filter is Suggestion → work with suggestions dataset
        if (filtersObj.status && filtersObj.status.value === "Suggestion") {
            let filtered = [...suggestions];

            if (filtersObj.group) {
                filtered = filtered.filter(s =>
                    s.itemDetails && s.itemDetails.itemGroup === filtersObj.group.value
                );
            }
            if (filtersObj.type) {
                filtered = filtered.filter(s =>
                    s.itemDetails && s.itemDetails.type === filtersObj.type.value
                );
            }
            if (filtersObj.createdBy) {
                filtered = filtered.filter(
                    s => s.createdByBadge === filtersObj.createdBy.value
                );
            }
            // Note: status filter "Suggestion" is *already* what chose this dataset

            if (search) {
                filtered = filtered.filter(s =>
                    Object.values(s)
                        .join(" ")
                        .toLowerCase()
                        .includes(search.toLowerCase())
                );
            }

            setFilteredItems(filtered);
            return;
        }

        // Normal item mode (existing behavior)
        let filtered = [...data];
        if (filtersObj.group) filtered = filtered.filter(item => item.itemGroup === filtersObj.group.value);
        if (filtersObj.type) filtered = filtered.filter(item => item.type === filtersObj.type.value);
        if (filtersObj.createdBy) filtered = filtered.filter(item => item.createdByBadge === filtersObj.createdBy.value);
        if (filtersObj.status) filtered = filtered.filter(item => item.status === filtersObj.status.value);
        if (search) {
            filtered = filtered.filter(item =>
                Object.values(item).join(" ").toLowerCase().includes(search.toLowerCase())
            );
        }
        setFilteredItems(filtered);
    };

    // Update filteredItems whenever filters or search change
    useEffect(() => {
        sessionStorage.setItem("approveInventoryFilters", JSON.stringify(filters));
        sessionStorage.setItem("approveInventorySearch", searchTerm);
        // Reset to page 1
        setPageIndex(0);
        applyFilters(items, filters, searchTerm);
    }, [filters, searchTerm, items, suggestions]);

    // Static filter options
    const typeOptions = [
        { value: "Asset", label: "Asset / Capital Nature" },
        { value: "Consumables", label: "Useable / Consumables" },
        { value: "Fixtures & Fittings", label: "Fixtures & Fittings" },
        { value: "Constructed Structure", label: "Constructed Structure" },
    ];

    const statusOptions = [
        { value: "Final", label: "Final" },
        { value: "Draft", label: "Draft" },
        { value: "On Hold", label: "On Hold" },
        { value: "Suggestion", label: "Suggestion" }, // NEW
    ];

    // Existing handlers (UNCHANGED)
    const handleApprove = async (row) => {
        const { id } = row;
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to approve this Inventory? This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, Approve it!",
            cancelButtonText: "Cancel",
        });

        if (confirmation.isConfirmed) {
            try {
                await apiClient.put(
                    `${SERVER_BASE}/approveItemMaster`,
                    { id, code, email },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                fetchItems();
                Swal.fire({
                    icon: "success",
                    title: "Inventory approved successfully!",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error("Error approved Inventory:", error);

                Swal.fire({
                    icon: "error",
                    title: "An error occurred while approved the Inventory.",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        }
    };

    const handleEdit = async (item) => {
        try {
            const response = await apiClient.post("/checkItemUsed", { itemId: item.id });

            if (response.data.used) {
                Swal.fire({
                    icon: "warning",
                    title: "This item is already in use and can not be edited",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            } else {
                navigate("../ItemMasterForm", { state: { item } });
            }
        } catch (error) {
            console.error("Error checking item usage:", error);
            Swal.fire({
                icon: "error",
                title: "Something went wrong!",
                toast: true,
                position: "top-end",
                timer: 3000,
                showConfirmButton: false,
            });
        }
    };

    const handleApproveSuggestion = (suggestionRow) => {
        navigate("../ItemMasterForm", {
          state: {
            item: suggestionRow?.itemDetails,
            suggestionData: suggestionRow,
            fromSuggestion: true,
          },
        });
      };

    const handleDelete = async (row) => {
        const { id, code } = row;

        try {
            const checkResponse = await apiClient.post(SERVER_BASE + "/checkItemUsed", { itemId: id });
            if (checkResponse.data.used) {
                Swal.fire({
                    icon: "warning",
                    title: "This item is already in use and cannot be deleted!",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
                return;
            }

            const confirmation = await Swal.fire({
                title: "Are you sure?",
                text: "Do you want to delete this Inventory? This action cannot be undone!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
            });

            if (confirmation.isConfirmed) {
                await apiClient.delete(SERVER_BASE + "/deleteItemMaster", {
                    data: { id, code },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                fetchItems();
                Swal.fire({
                    icon: "success",
                    title: "Inventory deleted successfully!",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error("Error deleting Inventory:", error);
            Swal.fire({
                icon: "error",
                title: "An error occurred while deleting the Inventory.",
                toast: true,
                position: "top-end",
                timer: 3000,
                showConfirmButton: false,
            });
        }
    };

    const handleReject = async (item) => {
        let selectedValues = [];

        const { value: formValues } = await Swal.fire({
            title: "Reject Item",
            html: `
            <div class="container text-start p-2">
              <div class="mb-3">
                <label for="rejectReason" class="form-label fw-bold">Hold Reasons</label>
                <select id="rejectReason" class="form-select">
                  <option value="">-- Select Reason --</option>
                  <option value="Confirm Inventory Name">Confirm Inventory Name</option>
                  <option value="Confirm Dimensions">Confirm Dimensions</option>
                  <option value="Confirm Color">Confirm Color</option>
                  <option value="Confirm Inventory Material">Confirm Inventory Material</option>
                  <option value="Confirm Brand Name">Confirm Brand Name</option>
                  <option value="Other">Other</option>
                </select>
                <div id="selectedReasons" class="mt-2 d-flex flex-wrap gap-2"></div>
              </div>
              <div class="mb-3">
                <label for="remarkText" class="form-label fw-bold">Remarks</label>
                <textarea id="remarkText" class="form-control" rows="4" placeholder="Enter your remarks here..."></textarea>
              </div>
            </div>
          `,
            showCancelButton: true,
            confirmButtonText: "Hold",
            cancelButtonText: "Cancel",
            focusConfirm: false,
            didOpen: () => {
                const select = document.getElementById("rejectReason");
                const container = document.getElementById("selectedReasons");

                select.addEventListener("change", () => {
                    const value = select.value;
                    if (value && !selectedValues.includes(value)) {
                        selectedValues.push(value);

                        const option = select.querySelector(`option[value="${value}"]`);
                        if (option) option.remove();

                        const badge = document.createElement("span");
                        badge.className =
                            "badge bg-primary d-flex align-items-center gap-1 p-2";
                        badge.innerHTML = `${value} <span style="cursor:pointer;">&times;</span>`;

                        badge.querySelector("span").addEventListener("click", () => {
                            selectedValues = selectedValues.filter((v) => v !== value);

                            const newOption = document.createElement("option");
                            newOption.value = value;
                            newOption.textContent = value;
                            select.appendChild(newOption);

                            badge.remove();
                        });

                        container.appendChild(badge);
                    }
                    select.value = "";
                });
            },
            preConfirm: () => {
                const remark = document.getElementById("remarkText").value.trim();

                if (selectedValues.length === 0) {
                    Swal.showValidationMessage(`Please select at least one Reject Reason`);
                    return false;
                }

                if (selectedValues.includes("Other") && !remark) {
                    Swal.showValidationMessage(
                        `Remarks are required when 'Other' is selected`
                    );
                    return false;
                }

                return { reasons: selectedValues.join(","), remark };
            },
        });

        if (formValues) {
            const { reasons, remark } = formValues;

            try {
                await apiClient.put(`${SERVER_BASE}/reject-item`, {
                    id: item.id,
                    reasons,
                    remark,
                    email,
                });

                fetchItems();

                Swal.fire({
                    icon: "success",
                    title: "Inventory moved on hold successfully!",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error("Error rejecting Inventory:", error);
                Swal.fire({
                    icon: "error",
                    title: "An error occurred while rejecting the Inventory.",
                    toast: true,
                    position: "top-end",
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        }
    };

    const handleAddDepartment = (item) => {
        navigate('../itemMaster', { state: { item: item, onlyDept: true } });
    };

    const handleClone = (item) => {
        navigate('../itemMaster', { state: { cloneItem: item } });
    };

    // NEW: Reject/Delete suggestion handler (you can wire API here)
    const handleRejectSuggestion = async (id) => {
        const confirmation = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you want to reject this suggestion? This action cannot be undone!',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, reject it!',
          cancelButtonText: 'Cancel',
        });
      
        if (confirmation.isConfirmed) {
          try {
            // Send the entire row data to the server
            await apiClient.put(SERVER_BASE+'/rejectSuggestion', {
              data: {id:id},
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              },
            });
    
            Swal.fire({
              icon: 'success',
              title: 'Suggestion rejected successfully!',
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            });
          } catch (error) {
            console.error('Error rejecting suggestion:', error);
    
          }
        }
      };
      const handleDeleteSuggestion = async (id) => {
        const confirmation = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you want to delete this suggestion? This action cannot be undone!',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it!',
          cancelButtonText: 'Cancel',
        });
      
        if (confirmation.isConfirmed) {
          try {
            // Send the entire row data to the server
            await apiClient.delete(SERVER_BASE+'/deleteSuggestion', {
              data: {id:id},
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              },
            });
    
            Swal.fire({
              icon: 'success',
              title: 'Suggestion deleted successfully!',
              toast: true,
              position: 'top-end',
              timer: 3000,
              showConfirmButton: false,
            }).then(()=>{
    
              fetchSuggestions()
            });
          } catch (error) {
            console.error('Error deleting suggestion:', error);
    
          }
        }
      };
    // Normal item columns
    const itemColumns = useMemo(
        () => [
            { Header: "Print Name", accessor: "printName" },
            { Header: "UOM", accessor: "uom" },
            { Header: "Weight", accessor: "avgWeight" },
            { Header: "Alias", accessor: "additionalNames" },
            { Header: "Depts", accessor: "concernedDepartments" },
            { Header: "Description", accessor: "itemDesc" },
            { Header: "Type", accessor: "type" },
            {
                Header: "Group",
                accessor: "itemGroupCode",
                Cell: ({ row }) => {
                    const { itemGroupCode, itemGroup } = row.original;
                    return `${itemGroupCode || ""} - ${itemGroup || ""}`;
                },
            },
            { Header: "Status", accessor: "status" },
            { Header: "Reason", accessor: "rejectedReason" },
            {
                Header: "Action",
                Cell: ({ row }) => {
                    const { createdByFirstName, createdAt, status } = row.original;
                    const item = row.original;
                    return (
                        <div className="dropdown text-center">
                            <button
                                className="btn btn-light btn-sm border"
                                type="button"
                                id={`dropdownMenuButton-${row.id}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </button>
                            <ul
                                className="dropdown-menu"
                                aria-labelledby={`dropdownMenuButton-${row.id}`}
                            >
                                {(status == "Draft") && (<>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleApprove(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faThumbsUp} className="me-2" />
                                            Approve
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleEdit(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                            Edit
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleDelete(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                                            Delete
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleReject(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                            Hold
                                        </button>
                                    </li>
                                </>)}
                                {(status == "On Hold") && (<>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleEdit(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                            Edit
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleApprove(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faThumbsUp} className="me-2" />
                                            Approve
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleDelete(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                                            Delete
                                        </button>
                                    </li>
                                </>)}
                                {(status == "Final") && (<>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleEdit(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                            Edit
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleDelete(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                                            Delete
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleAddDepartment(row.original)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Add Department
                                        </button>
                                    </li>
                                </>)}
                                {createdByFirstName && (
                                    <li className="dropdown-item">
                                        <FontAwesomeIcon icon={faPlus} /> {createdByFirstName}
                                        {createdAt &&
                                            ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                                    </li>
                                )}
                            </ul>
                        </div>
                    );
                },
            },
        ],
        []
    );

    // Suggestion columns (used only when Status filter = Suggestion)
    const suggestionColumns = useMemo(
        () => [
            { Header: "Associated Inventory", accessor: "printName" },
            { Header: "Inventory", accessor: "inventoryName" },
            { Header: "Size", accessor: "size" },
            { Header: "Color", accessor: "color" },
            { Header: "Made Of", accessor: "madeOf" },
            { Header: "Grade", accessor: "grade" },
            { Header: "Brand", accessor: "brandName" },
            { Header: "Avg Weight", accessor: "avgWeight" },
            { Header: "UOM", accessor: "uom" },
            { Header: "Remarks", accessor: "remarks" },
            { Header: "Status", accessor: "status" },
            {
                Header: "Action",
                Cell: ({ row }) => (
                    <div className="dropdown text-center">
                        <button
                            className="btn btn-light btn-sm border"
                            type="button"
                            data-bs-toggle="dropdown"
                        >
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                        <ul className="dropdown-menu">
                            {row.original.status === 'Suggested' ?
                                <li>
                                    <button
                                        className="dropdown-item"
                                        onClick={() => handleApproveSuggestion(row.original)}
                                    >
                                        <FontAwesomeIcon icon={faThumbsUp} className="me-2" />
                                        Action
                                    </button>
                                </li>
                                : null}
                            <li>
                                <button
                                    className="dropdown-item"
                                    onClick={() => handleRejectSuggestion(row.original.id)}
                                >
                                    <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                    Reject
                                </button>
                            </li>
                            <li>
                                <button
                                    className="dropdown-item"
                                    onClick={() => handleDeleteSuggestion(row.original.id)}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                    Delete
                                </button>
                            </li>
                        </ul>
                    </div>
                ),
            },
        ],
        []
    );

    // Choose columns based on status filter
    const columns = useMemo(
        () =>
            filters.status && filters.status.value === "Suggestion"
                ? suggestionColumns
                : itemColumns,
        [filters.status, itemColumns, suggestionColumns]
    );

    // Table instance
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
        state: tableState,
    } = useTable(
        {
            columns,
            data: filteredItems,
            initialState: { pageIndex: pageIndex, pageSize: 25 }, // default 25
        },
        useSortBy,
        usePagination
    );

    // Sync table pageIndex with sessionStorage
    useEffect(() => {
        setPageIndex(tableState.pageIndex);
        sessionStorage.setItem("approveInventorySearch", tableState.pageIndex);
    }, [tableState.pageIndex]);

    return (
        <div className="formcontainer mt-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Inventory Master Approval</h4>
            </div>

            {/* Search + Filters + Count */}
            <div className="card p-3 mb-3 shadow-sm">
                <div className="row g-3 align-items-end">
                    {/* Search */}
                    <div className="col-md-6">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Item Group */}
                    <div className="col-md-4">
                        <Select
                            options={itemGroups}
                            value={filters.group}
                            onChange={(selected) => setFilters({ ...filters, group: selected })}
                            placeholder='Item Group'
                            isClearable
                        />
                    </div>

                    {/* Page Size */}
                    <div className="col-md-2">
                        <select
                            className="form-select"
                            value={tableState?.pageSize || 10}
                            onChange={(e) => {
                                const newSize = Number(e.target.value);
                                setPageSize(newSize);
                            }}
                        >
                            {[...Array(8)].map((_, idx) => {
                                const val = (idx + 1) * 25; // 25, 50, 75, ..., 200
                                return (
                                    <option key={val} value={val}>
                                        {val} / page
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Type Filter */}
                    <div className="col-md-3">
                        <Select
                            options={typeOptions}
                            value={filters.type}
                            onChange={(selected) => setFilters({ ...filters, type: selected })}
                            placeholder='Type'
                            isClearable
                        />
                    </div>

                    {/* Created By Filter */}
                    <div className="col-md-3">
                        <Select
                            options={createdByOptions}
                            value={filters.createdBy}
                            onChange={(selected) => setFilters({ ...filters, createdBy: selected })}
                            placeholder='Created By'
                            isClearable
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="col-md-3">
                        <Select
                            options={statusOptions}
                            value={filters.status}
                            onChange={(selected) => setFilters({ ...filters, status: selected })}
                            placeholder='Status'
                            isClearable
                        />
                    </div>

                    {/* Record Count */}
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control text-center fw-semibold"
                            value={
                                filters.status && filters.status.value === "Suggestion"
                                    ? `Showing ${filteredItems.length} of ${suggestions.length} records`
                                    : `Showing ${filteredItems.length} of ${items.length} records`
                            }
                            readOnly
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-center my-5">Loading items...</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-hover" {...getTableProps()}>
                        <thead className="table-light" style={{ fontSize: '15px' }}>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                        >
                                            {column.render("Header")}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? " 🔽"
                                                        : " 🔼"
                                                    : ""}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()} style={{ fontSize: '13px', verticalAlign: 'baseline' }}>
                            {page.length > 0 ? (
                                page.map(row => {
                                    prepareRow(row);

                                    const status = row.original.status;
                                    let rowClass = "";
                                    if (status === "Final") rowClass = "text-success";
                                    else if (status === "Draft") rowClass = "text-primary";
                                    else if (status === "On Hold") rowClass = "text-danger";

                                    return (
                                        <tr {...row.getRowProps()} >
                                            {row.cells.map(cell => (
                                                <td className={rowClass} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-3">
                                        No records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            )}

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                {/* Previous on left */}
                <div>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        Previous
                    </button>
                </div>

                {/* Page info in center */}
                <div className="text-center flex-grow-1">
                    <span>
                        Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
                    </span>
                </div>

                {/* Next on right */}
                <div className="text-end">
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                    >
                        Next
                    </button>
                </div>
            </div>

        </div>
    );
};
export default ItemMasterApproval;
